import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { USER_CONVERSATION_ID } from '../../../api/conversations.api';
import { queryKeys } from '../../../api/queryKeys';
import { reportError } from '../../../helpers/reportError';
import {
  ChatActionOptionKey,
  ChatQuickReplyKey,
} from '../../../types/supportTicket.types';

/**
 * Which button on a triage card is waiting on the server. An option key stands
 * in for `confirm` on a card that offers several ways to say yes, so the one
 * that was pressed is the one that spins - and the same goes for the buttons
 * under a question, Yes / No or a location.
 */
export type ChatCardActionKind =
  | 'confirm'
  | 'reject'
  /* Which order was picked, so that row is the one that lights up. */
  | `order:${number}`
  | ChatActionOptionKey
  | ChatQuickReplyKey;

/**
 * How long to wait for the socket to say what happened before pulling the
 * thread ourselves. Long enough for the backend to finish talking to the
 * providers on a slow confirm, short enough that a merchant staring at a dead
 * spinner gets an answer instead.
 */
const SOCKET_FALLBACK_MS = 6000;

/**
 * Pressing a button on a triage card.
 *
 * One instance per card rather than one per thread: a card owns its own busy
 * state, so nothing has to be threaded down through the list, and the identity
 * survives the row being replaced (`MessageList` keys on `clientMessageId`).
 *
 * Two rules from the spec shape the whole thing:
 *
 *   - the buttons go dark on the click, NOT on the response. Confirming runs
 *     the real action and can take seconds; a live button in the meantime is an
 *     invitation to press it twice.
 *   - the outcome is read from the card, which the socket updates - never from
 *     the response, which carries no state and answers 200 even for a request
 *     that had already expired and did nothing.
 *
 * Merchant only. The admin app renders these cards read-only, so it never calls
 * this - which is also why the cache key is the merchant's sentinel.
 */
export const useChatCardAction = () => {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<ChatCardActionKind | null>(null);
  const fallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFallback = () => {
    if (fallbackRef.current) clearTimeout(fallbackRef.current);
    fallbackRef.current = null;
  };

  const refreshThread = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.messages(USER_CONVERSATION_ID),
      }),
    [queryClient]
  );

  const run = useCallback(
    async (kind: ChatCardActionKind, request: () => Promise<unknown>) => {
      if (busy) return;
      setBusy(kind);

      try {
        await request();
        /* The socket normally answers first and `settle` cancels this. It fires
           when the event never came - a dropped connection, a tab that was
           asleep - so the card is never stuck spinning on a request that has
           actually gone through. */
        clearFallback();
        fallbackRef.current = setTimeout(() => {
          fallbackRef.current = null;
          refreshThread();
          setBusy(null);
        }, SOCKET_FALLBACK_MS);
      } catch (error) {
        /* Every 400 here means the same thing: this card is behind the server.
           Already handled, already has an order, no longer one of the options -
           all of them are answered by showing the merchant what is true now,
           not by an error they did nothing to cause. */
        if (axios.isAxiosError(error) && error.response?.status === 400) {
          refreshThread();
        } else {
          reportError(error);
        }
        setBusy(null);
      }
    },
    [busy, refreshThread]
  );

  /**
   * The card calls this when its entity moved on, which is the real end of the
   * wait: the socket has delivered the new state and the fallback is moot.
   */
  const settle = useCallback(() => {
    clearFallback();
    setBusy(null);
  }, []);

  useEffect(() => clearFallback, []);

  return { busy, run, settle };
};
